// =============================================================
//  ManageDocumentsScreen — Gestion des documents
//  - Tri par displayOrder (logique conservée)
//  - Flèches ↑/↓ : réordonne instantanément (optimistic UI)
//    puis persiste silencieusement en arrière-plan
//  - Drag & drop HTML5 natif (robuste) sur la poignée gauche :
//    drop = persist en silence, toast en bas si erreur
// =============================================================

import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { db } from '../private/firebaseConfig';
import DocumentLifecycleService from '../services/DocumentLifecycleService';

import AdminLayout from './web-ui/AdminLayout';
import {
  Card,
  Button,
  Badge,
  EmptyState,
  Loading,
  SegmentedSelect,
  SearchBar,
  StatCard,
} from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

const TYPE_TABS = [
  { value: 'all', label: 'Tous' },
  { value: 'concours', label: 'Concours' },
  { value: 'bords', label: 'Bords' },
  { value: 'documents', label: 'Documents' },
];

const COLLECTIONS = [
  { col: 'concours', type: 'concours' },
  { col: 'bords', type: 'bords' },
  { col: 'documents', type: 'documents' },
];

const collectionForType = (type) => type;

const hasDisplayOrder = (d) =>
  typeof d?.displayOrder === 'number' && Number.isFinite(d.displayOrder);
const ts = (d) => (d?.createdAt?.toDate ? d.createdAt.toDate().getTime() : 0);

const sortByOrder = (a, b) => {
  const aHas = hasDisplayOrder(a);
  const bHas = hasDisplayOrder(b);
  const at = ts(a);
  const bt = ts(b);
  if (!aHas && !bHas) return bt - at;
  if (!aHas && bHas) return -1;
  if (aHas && !bHas) return 1;
  if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
  return bt - at;
};

export default function ManageDocumentsScreen({ navigation }) {
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [toast, setToast] = useState(null); // { msg, kind }
  const toastTimerRef = useRef(null);

  // Drag state
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const dragSrcRef = useRef(null);

  // ----- Toast helper -----
  const showToast = useCallback((msg, kind = 'success') => {
    setToast({ msg, kind });
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // ----- Chargement initial -----
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        COLLECTIONS.map(async ({ col, type }) => {
          const snap = await getDocs(collection(db, col));
          return snap.docs.map((d) => ({ id: d.id, type, ...d.data() }));
        })
      );
      setDocuments(results.flat());
    } catch (e) {
      console.log('loadAll documents error', e?.message || e);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ----- Actions simples -----
  const handleToggleStatus = async (docItem) => {
    const next = docItem.status === 'active' ? 'inactive' : 'active';
    // optimistic
    setDocuments((prev) =>
      prev.map((d) =>
        d.id === docItem.id && d.type === docItem.type ? { ...d, status: next } : d
      )
    );
    try {
      await updateDoc(doc(db, collectionForType(docItem.type), docItem.id), { status: next });
    } catch (e) {
      console.log('toggle status error', e?.message || e);
      showToast('Impossible de changer le statut', 'error');
      // rollback
      setDocuments((prev) =>
        prev.map((d) =>
          d.id === docItem.id && d.type === docItem.type ? { ...d, status: docItem.status } : d
        )
      );
    }
  };

  const handleDelete = async (docItem) => {
    const label = docItem.titre || docItem.sigle || 'ce document';

    // Un document déjà acheté ne doit jamais être détruit : on interroge
    // d'abord les achats pour choisir entre suppression et archivage.
    const safety = await DocumentLifecycleService.checkDeletionSafety(docItem.id);

    // Vérification impossible (réseau) → on ne prend aucun risque.
    if (!safety.canDelete && safety.purchaseCount === 0) {
      showToast(safety.reason, 'error');
      return;
    }

    const hasPurchases = safety.purchaseCount > 0;
    const message = hasPurchases
      ? `${safety.reason}\n\nArchiver "${label}" ?`
      : `Aucun achat pour "${label}". Le supprimer définitivement ?`;

    if (!window.confirm(message)) return;

    const backup = documents;
    // optimistic : dans les deux cas l'item quitte la liste du catalogue
    setDocuments((prev) =>
      prev.filter((d) => !(d.id === docItem.id && d.type === docItem.type))
    );
    try {
      const result = await DocumentLifecycleService.safeDelete(docItem.id, docItem.type);
      if (result.action === 'failed') {
        showToast(result.message, 'error');
        setDocuments(backup);
        return;
      }
      showToast(result.message, 'success');
    } catch (e) {
      console.log('delete error', e?.message || e);
      showToast('Suppression impossible', 'error');
      setDocuments(backup);
    }
  };

  // ===== Réordonnement =====
  // Écrit en batch le displayOrder de tous les items du type.
  // On écrit TOUT car l'état local a déjà été optimistically renuméroté,
  // donc on ne peut pas se fier à `d.displayOrder !== i` pour décider quoi écrire.
  const persistOrderForType = async (orderedSameTypeItems) => {
    if (!orderedSameTypeItems.length) return;
    const type = orderedSameTypeItems[0].type;
    const col = collectionForType(type);
    const batch = writeBatch(db);
    orderedSameTypeItems.forEach((d, i) => {
      batch.update(doc(db, col, d.id), { displayOrder: i });
    });
    await batch.commit();
  };

  // Réordonne l'état local en respectant les filtres : on ne touche QUE
  // l'ordre relatif des items du même type, peu importe les filtres
  // statut/recherche. Le tri global se fait après par sortByOrder.
  const applyReorder = (sourceId, sourceType, targetIndex) => {
    setDocuments((prev) => {
      const sameType = prev.filter((d) => d.type === sourceType).sort(sortByOrder);
      const otherType = prev.filter((d) => d.type !== sourceType);
      const fromIdx = sameType.findIndex((d) => d.id === sourceId);
      if (fromIdx < 0) return prev;
      const target = Math.max(0, Math.min(targetIndex, sameType.length - 1));
      if (fromIdx === target) return prev;
      const next = [...sameType];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(target, 0, moved);
      // Renumérote localement pour que le rendu suive immédiatement
      const renumbered = next.map((d, i) => ({ ...d, displayOrder: i }));
      return [...renumbered, ...otherType];
    });
  };

  // Flèche ↑/↓ : optimistic + persistance silencieuse en arrière-plan
  const moveDocument = (docItem, direction) => {
    const type = docItem.type;
    // On calcule la liste actuelle du même type pour trouver les indices
    const sameType = documents.filter((d) => d.type === type).sort(sortByOrder);
    const idx = sameType.findIndex((d) => d.id === docItem.id);
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= sameType.length) return;

    applyReorder(docItem.id, type, target);

    // Persistance asynchrone — on attend un tick pour avoir le state mis à jour
    setTimeout(() => {
      setDocuments((curr) => {
        const ordered = curr.filter((d) => d.type === type).sort(sortByOrder);
        persistOrderForType(ordered).catch((e) => {
          console.log('persistOrder error', e?.message || e);
          showToast('Réordonnement non sauvegardé', 'error');
          loadAll();
        });
        return curr;
      });
    }, 0);
  };

  // Drag & drop natif HTML5
  const onDragStart = (item) => (e) => {
    dragSrcRef.current = item;
    setDraggingId(item.id);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', item.id);
    } catch {}
  };
  const onDragOver = (item) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const src = dragSrcRef.current;
    if (src && src.type === item.type && src.id !== item.id) {
      setDragOverId(item.id);
    }
  };
  const onDragLeave = (item) => () => {
    if (dragOverId === item.id) setDragOverId(null);
  };
  const onDrop = (item) => (e) => {
    e.preventDefault();
    const src = dragSrcRef.current;
    dragSrcRef.current = null;
    setDraggingId(null);
    setDragOverId(null);
    if (!src || src.id === item.id || src.type !== item.type) return;

    // Position cible dans la liste du même type
    const sameType = documents.filter((d) => d.type === src.type).sort(sortByOrder);
    const toIdx = sameType.findIndex((d) => d.id === item.id);
    if (toIdx < 0) return;

    applyReorder(src.id, src.type, toIdx);

    setTimeout(() => {
      setDocuments((curr) => {
        const ordered = curr.filter((d) => d.type === src.type).sort(sortByOrder);
        persistOrderForType(ordered).catch((e2) => {
          console.log('persistOrder error', e2?.message || e2);
          showToast('Réordonnement non sauvegardé', 'error');
          loadAll();
        });
        return curr;
      });
    }, 0);
  };
  const onDragEnd = () => {
    dragSrcRef.current = null;
    setDraggingId(null);
    setDragOverId(null);
  };

  // ----- Tri d'affichage -----
  const filtered = useMemo(() => {
    const list = documents.filter((d) => {
      if (typeFilter !== 'all' && d.type !== typeFilter) return false;
      if (statusFilter === 'active' && d.status !== 'active') return false;
      if (statusFilter === 'inactive' && d.status === 'active') return false;
      const q = search.trim().toLowerCase();
      if (!q) return true;
      const title = (d.titre || d.sigle || d.nomComplet || '').toLowerCase();
      const desc = (d.description || '').toLowerCase();
      return title.includes(q) || desc.includes(q);
    });
    list.sort(sortByOrder);
    return list;
  }, [documents, typeFilter, statusFilter, search]);

  const stats = useMemo(
    () => ({
      total: documents.length,
      active: documents.filter((d) => d.status === 'active').length,
      inactive: documents.filter((d) => d.status !== 'active').length,
    }),
    [documents]
  );

  return (
    <AdminLayout
      title="Gestion des documents"
      subtitle="Concours, bords et documents — glisse la poignée gauche pour réordonner"
    >
      <View style={styles.statsRow}>
        <StatCard
          label="Total documents"
          value={stats.total}
          icon="file-document-multiple"
          accent={colors.primary}
          loading={loading}
        />
        <StatCard
          label="Actifs"
          value={stats.active}
          icon="check-circle-outline"
          accent={colors.success}
          loading={loading}
        />
        <StatCard
          label="Inactifs"
          value={stats.inactive}
          icon="cancel"
          accent={colors.warning}
          loading={loading}
        />
      </View>

      <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: spacing.md }}>
        <Button
          icon="cloud-upload-outline"
          onPress={() => navigation.navigate('PublishDocumentScreen')}
        >
          Publier un document
        </Button>
      </View>

      <Card style={{ marginBottom: spacing.md, gap: spacing.md }}>
        <SegmentedSelect options={TYPE_TABS} value={typeFilter} onChange={setTypeFilter} />
      </Card>

      <View style={styles.toolbar}>
        <View style={{ flex: 1, minWidth: 240 }}>
          <SearchBar value={search} onChangeText={setSearch} placeholder="Rechercher un document..." />
        </View>
        <SegmentedSelect
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'Tous' },
            { value: 'active', label: 'Actifs' },
            { value: 'inactive', label: 'Inactifs' },
          ]}
        />
      </View>

      <Card noPadding>
        {loading ? (
          <Loading label="Chargement..." />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="file-document-outline"
            title="Aucun document"
            subtitle="Essaie d'ajuster les filtres ou publie un nouveau document"
          />
        ) : (
          filtered.map((d, i) => {
            const title = d.sigle || d.titre || d.nomComplet || 'Sans titre';
            const isActive = d.status === 'active';
            const isDragging = draggingId === d.id;
            const isDragOver = dragOverId === d.id;
            return (
              <div
                key={`${d.type}-${d.id}`}
                draggable
                onDragStart={onDragStart(d)}
                onDragOver={onDragOver(d)}
                onDragLeave={onDragLeave(d)}
                onDrop={onDrop(d)}
                onDragEnd={onDragEnd}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  borderBottom: i === filtered.length - 1 ? 'none' : `1px solid ${colors.border}`,
                  background: isDragOver
                    ? colors.primarySoft
                    : isDragging
                    ? colors.surfaceAlt
                    : 'transparent',
                  opacity: isDragging ? 0.5 : 1,
                  transition: 'background 0.12s ease, opacity 0.12s ease',
                }}
              >
                {/* Poignée drag */}
                <div
                  title="Maintiens et glisse pour réordonner"
                  style={{
                    width: 22,
                    alignSelf: 'stretch',
                    cursor: 'grab',
                    color: isDragOver ? colors.primary : colors.textMuted,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderLeft: `3px solid ${isDragOver ? colors.primary : colors.border}`,
                    paddingLeft: 4,
                    userSelect: 'none',
                  }}
                >
                  <MaterialCommunityIcons name="drag-vertical" size={20} />
                </div>

                <View style={styles.thumb}>
                  <Text style={styles.thumbText}>{title.charAt(0).toUpperCase()}</Text>
                </View>

                <View style={{ flex: 1, minWidth: 0 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Text style={styles.rowTitle} numberOfLines={1}>{title}</Text>
                    {hasDisplayOrder(d) && (
                      <Text style={styles.position}>#{d.displayOrder}</Text>
                    )}
                  </View>
                  <Text style={styles.rowSubtitle} numberOfLines={1}>
                    {d.description || d.nomComplet || '—'}
                  </Text>
                </View>

                <Badge
                  variant={
                    d.type === 'concours' ? 'primary' : d.type === 'bords' ? 'info' : 'neutral'
                  }
                >
                  {d.type}
                </Badge>
                <Badge variant={isActive ? 'success' : 'warning'}>
                  {isActive ? 'Actif' : 'Inactif'}
                </Badge>

                <TouchableOpacity
                  onPress={() => moveDocument(d, 'up')}
                  style={styles.arrowBtn}
                  disabled={i === 0}
                >
                  <MaterialCommunityIcons
                    name="arrow-up-bold"
                    size={16}
                    color={i === 0 ? colors.textMuted : colors.primary}
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => moveDocument(d, 'down')}
                  style={styles.arrowBtn}
                  disabled={i === filtered.length - 1}
                >
                  <MaterialCommunityIcons
                    name="arrow-down-bold"
                    size={16}
                    color={i === filtered.length - 1 ? colors.textMuted : colors.primary}
                  />
                </TouchableOpacity>

                <Button
                  size="sm"
                  variant="ghost"
                  icon={isActive ? 'eye-off-outline' : 'eye-outline'}
                  onPress={() => handleToggleStatus(d)}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon="pencil-outline"
                  onPress={() => navigation.navigate('EditDocumentScreen', { document: d })}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon="eye-arrow-right-outline"
                  onPress={() => navigation.navigate('DocumentDetailScreen', { document: d })}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon="trash-can-outline"
                  onPress={() => handleDelete(d)}
                />
              </div>
            );
          })
        )}
      </Card>

      {/* Toast en bas */}
      {!!toast && (
        <View
          style={[
            styles.toast,
            toast.kind === 'error' ? styles.toastError : styles.toastOk,
          ]}
        >
          <MaterialCommunityIcons
            name={toast.kind === 'error' ? 'alert-circle-outline' : 'check-circle-outline'}
            size={18}
            color="#fff"
          />
          <Text style={styles.toastText}>{toast.msg}</Text>
        </View>
      )}
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    marginBottom: spacing.lg,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  thumb: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: colors.primarySoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbText: { color: colors.primary, fontWeight: '700', fontSize: 18 },
  rowTitle: { fontSize: 14, fontWeight: '600', color: colors.text },
  rowSubtitle: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
  position: {
    fontSize: 11,
    color: colors.textMuted,
    fontWeight: '700',
    backgroundColor: colors.surfaceAlt,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  arrowBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAlt,
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: [{ translateX: '-50%' }],
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
    zIndex: 9999,
  },
  toastOk: { backgroundColor: colors.success },
  toastError: { backgroundColor: colors.danger },
  toastText: { color: '#fff', fontSize: 14, fontWeight: '600' },
});
